import { useEffect, useRef, useState, memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Copy, Trash2, Plus, Move } from 'lucide-react';
import { useStore } from './store';
import { useChatStore } from './chatStore';
import { TYPE_CONFIG } from './constants';
import { createPortal } from 'react-dom';

let movingNodeId = null;

const CustomNodeComponentInner = ({ id, data, selected }) => {
   const nodeData = data || {};
   const config = TYPE_CONFIG[nodeData.type] || TYPE_CONFIG.message;
   const Icon = config.icon;

   const duplicateNode = useStore((s) => s.duplicateNode);
   const deleteNode = useStore((s) => s.deleteNode);
   const addChildNode = useStore((s) => s.addChildNode);
   const setSelectedNodeId = useStore((s) => s.setSelectedNodeId);
   const attachSubtree = useStore((s) => s.attachSubtree);
   const selectedNodeId = useStore((s) => s.selectedNodeId);
   const isExecuting = useChatStore((s) => s.currentNodeId === id);

  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const menuRef = useRef(null);
  const menuPortalRef = useRef(null);
  const contextMenuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail !== id) setIsMoving(false);
    };
    window.addEventListener('node-move-mode', handler);
    return () => window.removeEventListener('node-move-mode', handler);
  }, [id]);

  const openMenu = () => {
    setShowAddMenu(true);
    const nodes = useStore.getState().nodes;
    useStore.getState().setNodes(nodes.map(n => ({ ...n, zIndex: n.id === id ? 1000 : 0 })));
  };
  const closeMenu = () => {
    setShowAddMenu(false);
    useStore.getState().setNodes(useStore.getState().nodes.map(n => ({ ...n, zIndex: 0 })));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && 
          menuPortalRef.current && !menuPortalRef.current.contains(event.target)) closeMenu();
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) closeContextMenu();
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') { closeMenu(); closeContextMenu(); setIsMoving(false); }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showAddMenu, showContextMenu]);

  const handleRightClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowContextMenu(true);
    useStore.getState().setNodes(
      useStore.getState().nodes.map(n => ({ ...n, zIndex: n.id === id ? 1000 : 0 }))
    );
  };

  const closeContextMenu = () => {
    setShowContextMenu(false);
    useStore.getState().setNodes(useStore.getState().nodes.map(n => ({ ...n, zIndex: 0 })));
  };

  const handleStartMove = () => {
    closeContextMenu();
    setIsMoving(true);
    movingNodeId = id;
    window.dispatchEvent(new CustomEvent('node-move-mode', { detail: id }));
  };

  const isWebhookResult = nodeData.type === 'webhook_result';
  const isSystemNode = false;
  const resultColor = nodeData.webhookBranch === 'success' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-500';
  const resultBorder = nodeData.webhookBranch === 'success' ? 'border-emerald-200' : 'border-rose-200';

  const handleNodeClick = (e) => {
    if (movingNodeId && movingNodeId !== id) {
      e.stopPropagation();
      attachSubtree(movingNodeId, id);
      movingNodeId = null;
      window.dispatchEvent(new CustomEvent('node-move-mode', { detail: null }));
      return;
    }
    setSelectedNodeId(id);
  };

  const isSelected = selected || selectedNodeId === id;

  return (
    <div
      onClick={handleNodeClick}
      onContextMenu={handleRightClick}
      className={`relative group px-4 py-3 rounded-[24px] border-2 bg-white shadow-xl w-[220px] h-[72px] transition-all select-none ${
        isWebhookResult ? resultBorder : isSelected ? 'ring-8 ring-indigo-50 border-indigo-500' : 'hover:border-indigo-200 border-slate-100'
      } ${isExecuting ? 'ring-8 ring-indigo-100/50 border-indigo-600 shadow-indigo-100' : ''} ${
        isMoving ? 'ring-4 ring-amber-400 border-amber-400' : ''
      } ${
        movingNodeId && movingNodeId !== id ? 'cursor-crosshair ring-4 ring-emerald-200 hover:ring-emerald-400' : ''
      }`}
    >
      {nodeData.type !== 'start' && (
        <Handle
          type="target"
          position={Position.Left}
          className="target-handle"
        />
      )}
      
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-2xl ${isWebhookResult ? resultColor : config.color} flex items-center justify-center shadow-inner overflow-hidden`}>
          {typeof Icon === 'string' ? (
            <img src={Icon} alt="" className="w-full h-full object-contain scale-125" />
          ) : (
            <Icon size={22} className="opacity-90" />
          )}
        </div>
        <div className="flex-1 overflow-hidden">
          <p className="text-[9px] font-black text-slate-400/80 uppercase tracking-[0.15em] leading-none mb-2">
            {config.label} {nodeData.customId ? `#${nodeData.customId}` : ''}
          </p>
          <p className="text-sm font-bold text-slate-800 leading-tight truncate">
            {nodeData.label}
          </p>
        </div>
      </div>

      {nodeData.type === 'condition' && nodeData.branches?.map((b, i) => (
        <Handle
          key={b.id}
          type="source"
          position={Position.Right}
          id={b.id}
          style={{ top: `${(i + 1) * (100 / (nodeData.branches.length + 1))}%` }}
          className="source-handle !bg-slate-300"
        />
      ))}

      {nodeData.type === 'action' && (
        <>
          <Handle
            type="source"
            position={Position.Right}
            id="success"
            style={{ top: '30%' }}
            className="source-handle !bg-emerald-400"
          />
          <Handle
            type="source"
            position={Position.Right}
            id="failure"
            style={{ top: '70%' }}
            className="source-handle !bg-rose-400"
          />
        </>
      )}

      {nodeData.type !== 'condition' && nodeData.type !== 'action' && (
        <Handle
          type="source"
          position={Position.Right}
          className="source-handle"
        />
      )}

      {!isSystemNode && (
        <div className="absolute -right-8 top-1/2 -translate-y-1/2 z-[9999]" ref={menuRef}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              showAddMenu ? closeMenu() : openMenu();
            }}
            className={`w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-all border-2 border-white ${
              showAddMenu ? 'opacity-100 scale-110' : 'opacity-0 group-hover:opacity-100'
            }`}
          >
            <Plus size={14} />
          </button>
        </div>
      )}

      {showAddMenu && menuRef.current && createPortal(
        <div 
          ref={menuPortalRef}
          className="fixed bg-white border border-slate-200 rounded-2xl shadow-2xl p-1.5 min-w-[160px] grid grid-cols-1 gap-1 z-[9999]"
          style={{ 
            left: menuRef.current.getBoundingClientRect().right + 8,
            top: menuRef.current.getBoundingClientRect().top,
          }}
        >
          {Object.entries(TYPE_CONFIG).filter(([type]) => type !== 'start' && type !== 'webhook_result').map(([type, cfg]) => {
            const ItemIcon = cfg.icon;
            return (
              <button
                key={type}
                onClick={(e) => {
                  e.stopPropagation();
                  addChildNode(id, type);
                  closeMenu();
                }}
                className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 rounded-xl transition-colors group/item text-left"
              >
                <div className={`w-7 h-7 rounded-lg ${cfg.color} flex items-center justify-center`}>
                  <ItemIcon size={14} />
                </div>
                <span className="text-[11px] font-bold text-slate-600">{cfg.label}</span>
              </button>
            );
          })}
        </div>,
        document.body
      )}

      {!isSystemNode && (
        <div className={`absolute -top-10 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-white border border-slate-200 rounded-full p-1 shadow-2xl transition-opacity z-[100] ${
          selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`}>
          <button 
            onClick={(e) => { e.stopPropagation(); duplicateNode(id); }}
            className="p-1.5 hover:bg-slate-50 rounded-full text-slate-400 hover:text-indigo-500"
            title="Duplicate Node"
          >
            <Copy size={14} />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); deleteNode(id); }}
            className="p-1.5 hover:bg-slate-50 rounded-full text-slate-400 hover:text-rose-500"
            title="Delete Node"
          >
            <Trash2 size={14} />
          </button>
        </div>
      )}
      
      {isMoving && (
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-amber-400 text-white text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full shadow-lg">
          Click target node to attach
        </div>
      )}

      {showContextMenu && (
        <div
          ref={contextMenuRef}
          className="absolute left-full top-1/2 -translate-y-1/2 ml-3 bg-white border border-slate-200 rounded-2xl shadow-2xl p-1.5 min-w-[180px] z-[1000]"
          onClick={e => e.stopPropagation()}
        >
          <button
            onClick={(e) => { e.stopPropagation(); handleStartMove(); }}
            className="flex items-center gap-3 w-full px-3 py-2 hover:bg-indigo-50 rounded-xl transition-colors text-left"
          >
            <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600">
              <Move size={14} />
            </div>
            <span className="text-[11px] font-bold text-slate-600">Move with children</span>
          </button>
        </div>
      )}
    </div>
  );
};

export const CustomNodeComponent = memo(CustomNodeComponentInner);
